import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Search,
  Filter,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Edit,
  Download,
  Upload,
  ChevronRight,
  BarChart3,
  Users,
  Calendar,
  Package,
  Settings,
  ArrowLeft,
  Trash2,
  MessageSquare,
  History,
  Layers,
  X,
  Building,
  Target,
  DollarSign,
  Shield,
  Factory,
  TrendingUp,
  Rocket,
  ChevronDown,
  ChevronUp,
  FileDown,
  Image as ImageIcon
} from "lucide-react";
// Import modular SDP components
import {
  StatusChangeModal,
  ParecerModal,
  UploadModal,
  RevisionModal,
  ComponentModal,
  AssigneeModal,
  EditSDPModal,
  ImageGalleryModal,
  CreateSDPModal,
  SDPDetailView,
  AuthImage
} from "../components/sdp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status configurations
const STATUS_CONFIG = {
  solicitacao_aberta: { label: "Solicitação Aberta", color: "bg-blue-100 text-blue-800", icon: FileText },
  em_analise_tecnica: { label: "Em Análise Técnica", color: "bg-yellow-100 text-yellow-800", icon: Search },
  aprovado_desenvolvimento: { label: "Aprovado p/ Desenvolvimento", color: "bg-purple-100 text-purple-800", icon: CheckCircle },
  em_desenvolvimento: { label: "Em Desenvolvimento", color: "bg-orange-100 text-orange-800", icon: Settings },
  prototipo_concluido: { label: "Protótipo Concluído", color: "bg-cyan-100 text-cyan-800", icon: Package },
  em_validacao: { label: "Em Validação", color: "bg-indigo-100 text-indigo-800", icon: Eye },
  liberado_producao: { label: "Liberado p/ Produção", color: "bg-green-100 text-green-800", icon: CheckCircle },
  cancelado: { label: "Cancelado", color: "bg-red-100 text-red-800", icon: XCircle },
  arquivado: { label: "Arquivado", color: "bg-gray-100 text-gray-800", icon: FileText },
};

const PRIORITY_CONFIG = {
  baixa: { label: "Baixa", color: "bg-slate-100 text-slate-700" },
  media: { label: "Média", color: "bg-blue-100 text-blue-700" },
  alta: { label: "Alta", color: "bg-orange-100 text-orange-700" },
  estrategica: { label: "Estratégica", color: "bg-red-100 text-red-700" },
};

const TYPE_CONFIG = {
  novo_produto: { label: "Novo Produto" },
  customizacao: { label: "Customização" },
  reducao_custo: { label: "Redução de Custo" },
  melhoria_tecnica: { label: "Melhoria Técnica" },
};

export default function SDP() {
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sdps, setSdps] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedSdp, setSelectedSdp] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailView, setShowDetailView] = useState(false);
  const [linhasProduto, setLinhasProduto] = useState([]);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [filters, setFilters] = useState({
    status: "",
    prioridade: "",
    tipo: "",
    search: "",
    assignee_type: ""
  });

  const isAdmin = user?.role === "admin";
  const isEngenharia = user?.role === "engenharia";
  const canCreate = isAdmin;
  const canEdit = isAdmin || isEngenharia;

  const headers = { Authorization: `Bearer ${token}` };

  // Fetch SDPs
  const fetchSdps = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", pagination.page);
      params.append("page_size", 20);
      if (filters.status) params.append("status", filters.status);
      if (filters.prioridade) params.append("prioridade", filters.prioridade);
      if (filters.tipo) params.append("tipo", filters.tipo);
      if (filters.search) params.append("cliente", filters.search);
      if (filters.assignee_type) params.append("assignee_type", filters.assignee_type);

      const response = await axios.get(`${API_URL}/api/sdp?${params}`, { headers });
      setSdps(response.data.items);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (error) {
      console.error("Error fetching SDPs:", error);
      if (error.response?.status === 403) {
        toast.error("Sem permissão para acessar o módulo SDP");
      }
    } finally {
      setLoading(false);
    }
  }, [token, pagination.page, filters]);

  // Fetch Stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sdp/stats`, { headers });
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  }, [token]);

  // Fetch Linhas de Produto
  const fetchLinhasProduto = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sdp/config/linhas-produto`, { headers });
      setLinhasProduto(response.data);
    } catch (error) {
      console.error("Error fetching linhas:", error);
    }
  }, [token]);

  // Fetch Users for responsavel selection
  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, { headers });
      setUsers(response.data.filter(u => u.role === "engenharia" || u.role === "admin"));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchSdps();
    fetchStats();
    fetchLinhasProduto();
    fetchUsers();
  }, [fetchSdps, fetchStats, fetchLinhasProduto, fetchUsers]);

  // Get SDP details
  const fetchSdpDetails = async (sdpId) => {
    try {
      const response = await axios.get(`${API_URL}/api/sdp/${sdpId}`, { headers });
      setSelectedSdp(response.data);
      setShowDetailView(true);
    } catch (error) {
      toast.error("Erro ao carregar detalhes da SDP");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-800">
                  Portal de Engenharia
                </h1>
                <p className="text-sm text-slate-500">
                  Sistema de Solicitação de Desenvolvimento de Produto (SDP)
                </p>
              </div>
            </div>
            {canCreate && (
              <Button onClick={() => setShowCreateModal(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Nova SDP
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="p-6">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Clock className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.em_andamento}</p>
                    <p className="text-sm text-slate-500">Em Andamento</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.concluidos}</p>
                    <p className="text-sm text-slate-500">Concluídos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">{stats.atrasados}</p>
                    <p className="text-sm text-slate-500">Atrasados</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <BarChart3 className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-800">
                      {stats.por_prioridade?.find(p => p.prioridade === "estrategica")?.count || 0}
                    </p>
                    <p className="text-sm text-slate-500">Estratégicos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <Input
                  placeholder="Buscar por cliente..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full"
                />
              </div>
              <Select value={filters.status || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.prioridade || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, prioridade: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Prioridade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {Object.entries(PRIORITY_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.tipo || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, tipo: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  {Object.entries(TYPE_CONFIG).map(([key, cfg]) => (
                    <SelectItem key={key} value={key}>{cfg.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filters.assignee_type || "all"} onValueChange={(v) => setFilters(prev => ({ ...prev, assignee_type: v === "all" ? "" : v }))}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Atribuído a" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="admin">Grupo Admin</SelectItem>
                  <SelectItem value="engenharia">Grupo Engenharia</SelectItem>
                  <SelectItem value="user">Usuário Específico</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => { setFilters({ status: "", prioridade: "", tipo: "", search: "", assignee_type: "" }); }}>
                Limpar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* SDP List */}
        <Card>
          <CardHeader>
            <CardTitle>Solicitações de Desenvolvimento</CardTitle>
            <CardDescription>
              {pagination.total} solicitação(ões) encontrada(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-10">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-blue-600 rounded-full animate-spin" />
              </div>
            ) : sdps.length === 0 ? (
              <div className="text-center py-10 text-slate-500">
                <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>Nenhuma SDP encontrada</p>
                {canCreate && (
                  <Button onClick={() => setShowCreateModal(true)} className="mt-4">
                    Criar Primeira SDP
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                {sdps.map((sdp) => {
                  const statusCfg = STATUS_CONFIG[sdp.status] || STATUS_CONFIG.solicitacao_aberta;
                  const priorityCfg = PRIORITY_CONFIG[sdp.prioridade] || PRIORITY_CONFIG.media;
                  const StatusIcon = statusCfg.icon;
                  
                  return (
                    <div
                      key={sdp.id}
                      className="p-4 border border-slate-200 rounded-lg hover:border-blue-300 hover:bg-slate-50 cursor-pointer transition-all"
                      onClick={() => fetchSdpDetails(sdp.id)}
                      data-testid={`sdp-item-${sdp.codigo}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className={`p-2 rounded-lg ${statusCfg.color.split(' ')[0]}`}>
                            <StatusIcon className={`w-5 h-5 ${statusCfg.color.split(' ')[1]}`} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-semibold text-blue-600">{sdp.codigo}</span>
                              <Badge className={priorityCfg.color}>{priorityCfg.label}</Badge>
                              <Badge variant="outline">{TYPE_CONFIG[sdp.tipo]?.label}</Badge>
                            </div>
                            <h3 className="font-medium text-slate-800 mt-1">{sdp.nome_projeto}</h3>
                            <div className="flex items-center gap-4 text-sm text-slate-500 mt-1 flex-wrap">
                              {sdp.cliente && <span>Cliente: {sdp.cliente}</span>}
                              {sdp.assignee_name && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  <span className="font-medium text-blue-600">{sdp.assignee_name}</span>
                                </span>
                              )}
                              {sdp.responsavel_tecnico_nome && !sdp.assignee_name && (
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {sdp.responsavel_tecnico_nome}
                                </span>
                              )}
                              {sdp.prazo_desejado && (
                                <span className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {sdp.prazo_desejado}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={statusCfg.color}>{statusCfg.label}</Badge>
                          <span className="text-slate-400">Rev. {sdp.revisao_atual || 0}</span>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Anterior
                </Button>
                <span className="flex items-center px-4 text-sm text-slate-600">
                  Página {pagination.page} de {pagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Próxima
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Create SDP Modal */}
      <CreateSDPModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          fetchSdps();
          fetchStats();
        }}
        linhasProduto={linhasProduto}
        users={users}
        token={token}
      />

      {/* SDP Detail View */}
      {showDetailView && selectedSdp && (
        <SDPDetailView
          sdp={selectedSdp}
          onClose={() => {
            setShowDetailView(false);
            setSelectedSdp(null);
          }}
          onUpdate={() => {
            fetchSdpDetails(selectedSdp.id);
            fetchSdps();
            fetchStats();
          }}
          token={token}
          canEdit={canEdit}
          isAdmin={isAdmin}
          linhasProduto={linhasProduto}
          users={users}
        />
      )}
    </div>
  );
}
